/**
 * Minimal Server-Sent-Events reader for fetch() responses (EventSource cannot
 * POST or send the Authorization header). Get the Response from
 * apiClient.streamPost — it carries the app's token handling — and pass it here.
 *
 * Follows the SSE framing rules that matter for our endpoints: events are
 * separated by a blank line, `event:` names the event (default "message"),
 * several `data:` lines are joined with "\n", lines starting with ":" are
 * comments (heartbeats) and are ignored, CRLF / CR line endings are accepted.
 */

export interface SseEvent {
  event: string;
  data: string;
}

function parseFrame(frame: string): SseEvent | null {
  let event = 'message';
  const data: string[] = [];
  for (const line of frame.split('\n')) {
    if (line === '' || line.startsWith(':')) continue;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') event = value;
    else if (field === 'data') data.push(value);
  }
  return data.length > 0 ? { event, data: data.join('\n') } : null;
}

/**
 * Reads the response body to its end and calls onEvent for every complete
 * event, in order. Resolves when the stream ends; rejects when reading fails
 * (e.g. the request was aborted — the AbortError is passed on).
 */
export async function readSseEvents(response: Response, onEvent: (event: SseEvent) => void): Promise<void> {
  if (!response.body) throw new Error('The response has no body to stream');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let heldCr = '';
  try {
    for (;;) {
      const { done, value } = await reader.read();
      let text = heldCr + (done ? decoder.decode() : decoder.decode(value, { stream: true }));
      heldCr = '';
      // A CR at the end of a chunk may be the first half of a CRLF: decide with the next chunk.
      if (!done && text.endsWith('\r')) {
        heldCr = '\r';
        text = text.slice(0, -1);
      }
      buffer += text.replace(/\r\n?/g, '\n');
      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const parsed = parseFrame(buffer.slice(0, boundary));
        buffer = buffer.slice(boundary + 2);
        if (parsed) onEvent(parsed);
        boundary = buffer.indexOf('\n\n');
      }
      if (done) break;
    }
    // A last event without the closing blank line still counts.
    const rest = parseFrame(buffer);
    if (rest) onEvent(rest);
  } finally {
    reader.releaseLock();
  }
}
